import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Calendar } from 'primeng/calendar';

import { takeUntil } from 'rxjs/operators';
import { ControlValueAccessorConnector } from '../controlvalue-accessor.connector';
import { NgbDateTimeStruct } from './date-input.consts';

@Component({
  selector: 'app-dc-date-input',
  templateUrl: './dc-date-input.component.html',
  styleUrls: ['./dc-date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DcDateInputComponent,
      multi: true,
    },
  ],
})
export class DcDateInputComponent extends ControlValueAccessorConnector {
  @Input() id: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() darkMode?: boolean;
  @Input() helpText?: string;
  @Input() container?: 'body' | '';
  @Input() showTicksIfValid: boolean; // off by default
  @Input() customErrorMessage: string;
  @Input() showTime: boolean;
  @Output() onFocusEvent = new EventEmitter<void>();

  @ViewChild(Calendar, { static: true })
  calendar: Calendar;

  displayForm = new UntypedFormGroup({
    displayDate: new UntypedFormControl(),
  });

  constructor(injector: Injector, private cd: ChangeDetectorRef) {
    super(injector);
  }

  ngOnInit(): void {
    this.control.valueChanges.subscribe((value) => {
      if (value) {
        this.onDateSelect(value, { emitEvent: false });
      } else {
        this.displayForm.patchValue(
          { displayDate: null },
          { emitEvent: false }
        );
      }
    });

    this.displayForm
      .get('displayDate')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.onDateChange(value);
      });

    this.onDateSelect(this.control.value);
  }

  ngAfterContentInit(): void {
    this.cd.detectChanges();
  }

  getValidationStatus(): string {
    return this.control.invalid
      ? 'invalid'
      : this.showTicksIfValid
      ? 'valid'
      : '';
  }

  onDateSelect(date: NgbDateTimeStruct, options: any = {}): void {
    if (date) {
      const { day, month, year, hour, minute } = date;
      const displayDate = new Date(year, month - 1, day);
      if (Number.isFinite(hour) && Number.isFinite(minute)) {
        displayDate.setHours(hour, minute);
      }
      this.displayForm.patchValue({ displayDate }, options);
    } else {
      if (date !== this.displayForm.get('displayDate').value) {
        this.displayForm.patchValue({ displayDate: date }, options);
      }
    }
  }

  onDateChange(date: Date): void {
    if (date) {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hour = date.getHours();
      const minute = date.getMinutes();

      this.control.setValue({ year, month, day, hour, minute });
    } else {
      this.control.setValue(null);
    }

    if (this.displayForm.touched) {
      this.control.markAsTouched();
    }

    if (this.displayForm.dirty) {
      this.control.markAsDirty();
      this.control.parent.markAsDirty();
    }
  }

  set readonly(readonly: boolean) {
    this._readonly = readonly;
    this.setDisabledState(readonly);
  }

  get readonly(): boolean {
    return this._readonly ?? this.control['readOnly'];
  }

  setDisabledState(isDisabled: boolean): void {
    this.displayForm.get('displayDate')[this._readonly || isDisabled ? 'disable' : 'enable']();
    super.setDisabledState(isDisabled);
  }

  // Template functions
  onFocus(): void {
    this.control.markAsTouched();

    this.onFocusEvent.emit();
  }
}
